import { WORLD_GEN } from "../constants";
import { clamp, vDist, type Vec2 } from "../math";
import { Rng, valueNoise2 } from "./rng";
import type { AsteroidDef, Biome, SunDef } from "./types";

/**
 * Where a rock is, as what grows on it cares about (see lifeasteroids.md,
 * "Environment"): each 0..1.
 * - light: from the warm suns (a cold sun gives some); what plants need.
 * - heat: from the nearest sun's surface; a cold sun gives light but little.
 * - wet: ice in the rock, nearness to the water ball.
 */
export interface Environment {
  light: number;
  heat: number;
  wet: number;
}

/** What the environment is worked out from: the global layer (see generateGlobal). */
export interface EnvironmentSources {
  suns: readonly SunDef[];
  /** The water ball, if there is one. */
  water?: Vec2;
}

/** The light a rock of `radius` at `center` gets, 0..1 (0 too close to a sun, where flares scorch everything off). See WORLD_GEN.life. */
export function lightAt(suns: readonly SunDef[], center: Vec2, radius: number): number {
  const { scorched, warm, dark, coldLight } = WORLD_GEN.life;
  let best = 0;
  for (const s of suns) {
    const gap = vDist(s.position, center) - s.radius - radius;
    if (gap < scorched && s.kind !== "cold") return 0;
    const light = gap < warm[0] ? Math.max(0, (gap - scorched) / (warm[0] - scorched)) : gap <= warm[1] ? 1 : Math.max(0, 1 - (gap - warm[1]) / (dark - warm[1]));
    best = Math.max(best, light * (s.kind === "cold" ? coldLight : 1));
  }
  return best;
}

/** How hot it is at a rock of `radius` at `center`, 0..1: 1 inside a flare's reach, falling to 0 by WORLD_GEN.life.heatReach (sooner than the light fades). */
export function heatAt(suns: readonly SunDef[], center: Vec2, radius: number): number {
  const { scorched, heatReach: dark } = WORLD_GEN.life;
  let best = 0;
  for (const s of suns) {
    const gap = vDist(s.position, center) - s.radius - radius;
    const h = gap <= scorched ? 1 : Math.max(0, 1 - (gap - scorched) / (dark - scorched));
    best = Math.max(best, h * (s.kind === "cold" ? WORLD_GEN.env.coldHeat : 1));
  }
  return best;
}

/** How wet it is at `center`, 0..1: near the water ball, wetter; some rocks are damp wherever they are; ice rocks are wet whatever. */
export function wetAt(src: EnvironmentSources, center: Vec2, kind: AsteroidDef["kind"], seed: number): number {
  const { waterReach, damp, dampWet, iceWet, jitter } = WORLD_GEN.env;
  let wet = src.water ? Math.max(0, 1 - vDist(src.water, center) / waterReach) : 0;
  const roll = new Rng(seed ^ 0xda4b).next();
  if (roll < damp) wet = Math.max(wet, dampWet[0] + (roll / damp) * (dampWet[1] - dampWet[0]));
  if (kind === "ice") wet = Math.max(wet, iceWet);
  // Two rocks side by side aren't quite alike.
  wet += (valueNoise2(seed + 71, 0.5, 0.5) - 0.5) * jitter;
  return clamp(wet, 0, 1);
}

/** Everything about where a rock is (see Environment), rounded so it reads well in a def. */
export function environmentAt(src: EnvironmentSources, center: Vec2, radius: number, kind: AsteroidDef["kind"], seed: number): Environment {
  const r = (x: number) => Math.round(x * 100) / 100;
  return { light: r(lightAt(src.suns, center, radius)), heat: r(heatAt(src.suns, center, radius)), wet: r(wetAt(src, center, kind, seed)) };
}

/**
 * The biome a rock is (see lifeasteroids.md, "Biomes"): picked from its
 * environment and its kind. Water, home and pirate bases keep their own.
 */
export function biomeOf(env: Environment, kind: AsteroidDef["kind"], pirateBase = false): Biome {
  const b = WORLD_GEN.biomes;
  if (kind === "home") return "meadow";
  if (kind === "water") return "water";
  if (kind === "living") return "living";
  // A nugget is bare ore or coal: nothing grows on it, and nothing covers it.
  if (pirateBase || kind === "coal" || kind === "iron") return "barren";
  if (env.heat >= b.scorched) return "scorched";
  if (kind === "dead") return env.light >= b.lichen ? "wreck" : "dark";
  // Ice rocks, and wet rocks out in the cold, freeze.
  if (kind === "ice" || (env.heat < b.cold && env.wet >= b.frostWet)) return env.light >= b.lichen || kind === "ice" ? "frost" : "dark";
  if (env.light >= b.meadow) return env.wet >= b.groveWet ? "grove" : "meadow";
  if (env.light >= b.twilight) return "twilight";
  if (env.light >= b.lichen) return "lichen";
  return "dark";
}

/** Which mineral zone a rock at this heat is in (see WORLD_GEN.minerals): the hot inner rings, the cold outer ones, or between. */
export function mineralZone(heat: number): "hot" | "mild" | "cold" {
  const M = WORLD_GEN.minerals;
  return heat >= M.hot ? "hot" : heat < M.cold ? "cold" : "mild";
}

/** Is a rock at this heat close enough to a warm sun to grow into a living rock? (Just outside a flare's reach.) */
export const livingHeat = (heat: number): boolean => heat >= WORLD_GEN.living.heat && heat < WORLD_GEN.biomes.scorched;
